import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import axios from '../axiosInstance';
import logoBanner from "../assets/images/new_logo_without_font.png";
import ReCAPTCHA from "react-google-recaptcha";
import HighPerformanceAd from "../components/HighPerformanceAd";

function Toast({ message, type = "error", onClose }) {
  return (
    <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded shadow-lg text-white ${type === "error" ? "bg-red-600" : "bg-green-600"}`}>
      <div className="flex items-center space-x-2">
        <span>{message}</span>
        <button onClick={onClose} className="ml-2 text-white hover:text-gray-300 font-bold">×</button>
      </div>
    </div>
  );
}

export default function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [referer, setReferer] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [toast, setToast] = useState({ message: "", type: "" });
  const recaptchaRef = useRef();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const adRef = useRef(null);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
  
  const showToast = (message, type = "error") => {
	  setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

  const navigate = useNavigate();

  const handleRegister = async () => {
	  

if (!acceptedTerms) {
   showToast("You must accept the Terms & Conditions.", "error");
   return
}	

	if (!captchaToken) {
      showToast("Please validate the captcha.", "error");
      return;
    }  
	 
    if (!email || !password || !confirmPassword) {
      showToast("All required fields must be filled.", "error");
       return
    }
   
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      // return alert("Invalid email address.");
	   showToast("Invalid email address.", "error");
     return;
    }
   
    if (password.length < 6) {
      // return alert("Password must be at least 6 characters long.");
	   showToast("Password must be at least 6 characters long.", "error");
     return;
    }
   
    if (password !== confirmPassword) {
      // return alert("Passwords do not match.");
	     showToast("Passwords do not match.", "error");
       return;
    }
   
    try {
      const response = await axios.post(
        `/api/register`,
        {
          email: email,
          password: password,
          referer: referer,   
          captchaToken: captchaToken,
          subscribeNewsletter: subscribeNewsletter, // ✅ Ajout ici
        },
        { withCredentials: true }
      );

		showToast("Account created successfully! You will be redirected in a few seconds.", "success")
   
    if (response.data.success) {
      setTimeout(() => {
        navigate('/login');
      }, 5000); // délai de 3,5 secondes
    }
    
    } catch (error) {
      console.error("Registration error:", error.message);
      const message = error.response?.data?.message || error.message || "Unknown error";
      showToast(message, "error");
      // alert(`Server error: ${error.message}`);
    }
  };

  const isFormValid =
    email && password && confirmPassword;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("refer");
    
    document.title = "Register | CoinzUp";
    
    if (ref) setReferer(ref);
  }, []);

  return (
    <>
    {toast.message && (
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "" })}
      />
    )}
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-gray-800 px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/index">
          <img src={logoBanner} alt="CoinZUp Logo" className="w-32 sm:w-40 h-auto" />
        </Link>
        <div className="space-x-3 sm:space-x-4 text-sm sm:text-base">
          <Link to="/blog" className="hover:underline">Blog</Link>
          <Link to="/login" className="hover:underline">Log In</Link>
          <Link to="/register" className="bg-yellow-500 text-black px-4 py-2 rounded hover:bg-yellow-400 transition">
            Sign Up
          </Link>
        </div>
      </header>

    {/* Ad or Banner Placeholder */}
        {/* 🄸🄵🅁🄰🄼🄴 🔽 always visible and responsive */}
        <HighPerformanceAd keyId="fd5da3543c594f07b73630f461f10613" width={728} height={90}/>

      {/* Register Form Section */}
      <main className="flex-grow flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-xl">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Create Your Account</h2>

          <input
            type="email"
            placeholder="Email"
            className="w-full p-3 mb-3 rounded bg-gray-800 text-white"
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password"
            className="w-full p-3 mb-3 rounded bg-gray-800 text-white"
            onChange={(e) => setPassword(e.target.value)}
          />
          <input
            type="password"
            placeholder="Confirm Password"
            className="w-full p-3 mb-3 rounded bg-gray-800 text-white"
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <input
            type="text"
            placeholder="Referrer (optional)"
            className="w-full p-3 mb-3 rounded bg-gray-800 text-white"
            value={referer}
            onChange={(e) => setReferer(e.target.value)}
          />          
          <ReCAPTCHA
            sitekey="6LeRXTErAAAAABw214tL2nCwxDvCJ-EKAfoAkUSg"
            ref={recaptchaRef}
            onChange={(token) => setCaptchaToken(token)}
          />
          <div className="flex items-start mt-4 space-x-2 text-sm text-gray-400">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1"
            />
            <label htmlFor="terms">
              I agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-blue-400 hover:text-blue-300"
              >
                Terms & Conditions
              </a>
            </label>
          </div>
          <div className="flex items-start mt-2 space-x-2 text-sm text-gray-400">
            <input
              type="checkbox"
              id="newsletter"
              checked={subscribeNewsletter}
              onChange={(e) => setSubscribeNewsletter(e.target.checked)}
              className="mt-1"
            />
            <label htmlFor="newsletter">
              I want to receive the CoinzUp newsletter and updates.
            </label>
          </div>
          <button
            className={`w-full mt-4 bg-yellow-500 text-black px-4 py-2 rounded hover:bg-yellow-400 transition ${
              !isFormValid && "opacity-50 cursor-not-allowed"
            }`}
            onClick={handleRegister}
            disabled={!isFormValid}
          >
            Register
          </button>
          
          <div className="text-center text-sm text-gray-400 mt-4">
            Already registered?{" "}
            <span
              className="text-blue-400 underline cursor-pointer"
              onClick={() => navigate("/login")}
            >
              Log in
            </span>
          </div>
        </div>
      </main>
      <HighPerformanceAd keyId="96e99453a24be0353f2fe26dc0b9b728" width={468} height={60}/>
      {/* Footer */}
      <footer className="bg-gray-900/80 text-center text-sm text-gray-500 py-4 border-t border-gray-800 space-x-2">
        © 2025 CoinzUp.net. All rights reserved. 
        <Link to="/notice" className="hover:text-white underline">Privacy & Legal Notice</Link>
        <Link to="/terms" className="hover:text-white underline">Terms & Conditions</Link>
      </footer>
    </div>
    </>
  );
}
